import { useEffect, useMemo, useRef, useState } from "react";
import {
  useAdminPartners,
  useAdminPartnerDetail,
  useAdminPartnerUsers,
  useAdminCreatePartner,
  useAdminUpdatePartner,
  useAdminCreatePartnerKey,
  useAdminDisablePartnerKey,
  useAdminDeletePartner,
  useAdminMe,
  useAdminPlans,
  useAdminUsers,
} from "../hooks/useApi";
import { formatNumber, decimalToPlainString } from "../utils/format";
import "../admin-theme.css";

interface SimpleCopyButtonProps {
  text: string;
}

function SimpleCopyButton({ text }: SimpleCopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  if (copied) {
    return <span className="text-xs" style={{ color: "var(--admin-success)" }}>کپی شد ✓</span>;
  }
  return (
    <button
      onClick={handleCopy}
      className="admin-btn admin-btn-secondary"
      style={{ padding: "6px 12px", fontSize: 12 }}
    >
      کپی کلید
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "active") {
    return <span className="admin-badge admin-badge-success">فعال</span>;
  }
  return <span className="admin-badge admin-badge-warning">غیرفعال</span>;
}

function KeyStatusBadge({ status }: { status: string }) {
  return status === "active" ? (
    <span className="admin-badge admin-badge-success" style={{ fontSize: 10 }}>فعال</span>
  ) : (
    <span className="admin-badge admin-badge-warning" style={{ fontSize: 10 }}>غیرفعال</span>
  );
}

const EMPTY_FIELDS = { name: "", code: "", initial_balance: "", plan_id: "", representative_user_id: "" };

interface RepresentativePickerProps {
  value: string;
  onChange: (userId: string) => void;
  allowClear?: boolean;
  placeholder?: string;
}

/** Search-and-select an existing Asha user as the partner's representative. */
function RepresentativePicker({ value, onChange, allowClear = true, placeholder }: RepresentativePickerProps) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const params = useMemo(() => {
    const p: Record<string, unknown> = { limit: 20, sort: "newest" };
    if (debounced) p.search = debounced;
    return p;
  }, [debounced]);

  const { data = [], isLoading } = useAdminUsers(params);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const users: Record<string, any>[] = Array.isArray(data) ? data : [];

  let selectedLabel = "";
  if (value) {
    const match = users.find((u) => String(u.id) === value);
    selectedLabel = userLabel(match ?? null, value);
  }

  return (
    <div ref={boxRef} className="relative" dir="rtl">
      <input
        type="text"
        value={open ? query : selectedLabel || query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="admin-input"
        placeholder={placeholder ?? "جستجوی نام، تلفن یا ایمیل کاربر..."}
      />
      {allowClear && value && (
        <button
          type="button"
          onClick={() => { onChange(""); setQuery(""); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-xs p-1 rounded"
          style={{ color: "var(--admin-text-tertiary)" }}
          title="حذف نماینده"
        >
          ✕
        </button>
      )}
      {open && (
        <div
          className="absolute z-30 mt-1 w-full rounded-xl overflow-hidden"
          style={{ background: "var(--admin-bg)", border: "1px solid var(--admin-border)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}
        >
          {isLoading ? (
            <div className="px-3 py-2 text-xs" style={{ color: "var(--admin-text-tertiary)" }}>در حال جستجو...</div>
          ) : !users.length ? (
            <div className="px-3 py-2 text-xs" style={{ color: "var(--admin-text-tertiary)" }}>کاربری یافت نشد</div>
          ) : (
            <div className="max-h-56 overflow-y-auto">
              {users.map((u) => {
                const label = userLabel(u, String(u.id));
                return (
                  <button
                    key={u.id}
                    type="button"
                    className="w-full text-right px-3 py-2 hover:opacity-80 transition-opacity"
                    style={{ background: String(u.id) === value ? "var(--admin-bg-hover)" : "transparent", borderBottom: "1px solid var(--admin-border)" }}
                    onClick={() => { onChange(String(u.id)); setQuery(""); setOpen(false); }}
                  >
                    <span className="block text-sm" style={{ color: "var(--admin-text-primary)" }}>
                      <span className="font-number">#{u.id}</span> {label || "—"}
                    </span>
                    {u.phone && (
                      <span dir="ltr" className="block font-mono text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                        {u.phone}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function userLabel(user: Record<string, any> | null, fallback: string): string {
  if (!user) return fallback;
  const parts = [user.name, user.family_name].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return user.username || user.email || fallback;
}

export default function AdminPartners() {
  const { data: me } = useAdminMe();
  const canManage = Boolean(me?.is_admin) || Boolean(me?.permissions?.includes("partners.manage"));

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const overlayMouseDown = useRef(false);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page_size: 50 };
    if (debouncedSearch) p.search = debouncedSearch;
    if (statusFilter) p.partner_status = statusFilter;
    return p;
  }, [debouncedSearch, statusFilter]);

  const { data, isLoading, refetch } = useAdminPartners(params);
  const createPartner = useAdminCreatePartner();
  const detailQuery = useAdminPartnerDetail(selectedId);
  const { data: plans = [] } = useAdminPlans();

  const partners: Record<string, unknown>[] = data?.items || [];
  const total = data?.total || 0;

  const totalBalance = useMemo(
    () => partners.reduce((s: number, p) => s + Number(p.balance || 0), 0),
    [partners]
  );

  const handleCreate = () => {
    const name = fields.name.trim();
    const code = fields.code.trim().toLowerCase().replace(/\s+/g, "");
    const balance = Number(decimalToPlainString(fields.initial_balance));
    const plan_id = fields.plan_id ? Number(fields.plan_id) : null;
    const representative_user_id = fields.representative_user_id
      ? Number(fields.representative_user_id)
      : null;
    if (!name || !code) return;
    createPartner.mutate(
      {
        name,
        code,
        initial_balance: Number.isFinite(balance) && balance > 0 ? Math.trunc(balance) : 0,
        currency: "IRR",
        plan_id,
        representative_user_id,
      },
      {
        onSuccess: () => {
          setShowModal(false);
          setFields(EMPTY_FIELDS);
          refetch();
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>شرکا (Partner)</h1>
          <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>
            پلتفرم‌های بیرونی که کاربران خود را از طریق API اختصاصی وصل می‌کنند و موجودی از پیش‌پرداخت‌شده دارند
          </p>
        </div>
        {canManage && (
          <button className="admin-btn admin-btn-primary" onClick={() => { setFields(EMPTY_FIELDS); setShowModal(true); }}>
            + شریک جدید
          </button>
        )}
      </div>

      {/* Total Balance Card */}
      <div className="admin-metric-card">
        <div className="admin-metric-card-accent admin-metric-card-accent-dark" />
        <div className="admin-metric-card-content">
          <div>
            <span className="text-xs font-medium" style={{ color: "var(--admin-text-tertiary)" }}>مجموع موجودی شرکا</span>
            <span className="block text-2xl font-bold mt-2" style={{ color: "var(--admin-text-primary)" }}>
              {formatNumber(totalBalance)}
              <span className="text-sm font-normal mr-2" style={{ color: "var(--admin-text-tertiary)" }}>ریال</span>
            </span>
          </div>
        </div>
      </div>

      {/* Partners Table */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header flex flex-wrap items-center gap-3 justify-between">
          <h2 className="admin-card-title">لیست شرکا</h2>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-input"
              style={{ width: 220 }}
              placeholder="جستجوی نام یا کد..."
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="admin-input"
              style={{ width: 140 }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="active">فعال</option>
              <option value="disabled">غیرفعال</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !partners.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-50" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 10h18" />
              <path d="M8 14h3" />
            </svg>
            <p className="text-sm">هیچ شریکی ثبت نشده است</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>نام</th>
                  <th>کد</th>
                  <th>پلن</th>
                  <th>نماینده</th>
                  <th>موجودی (ریال)</th>
                  <th>کلیدها</th>
                  <th>کاربران</th>
                  <th>وضعیت</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p: Record<string, unknown>) => (
                  <tr key={p.id as number}>
                    <td>
                      <span className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>
                        {p.name as string}
                      </span>
                    </td>
                    <td>
                      <code dir="ltr" className="font-mono text-xs" style={{ color: "var(--admin-text-secondary)" }}>
                        {p.code as string}
                      </code>
                    </td>
                    <td>
                      {(p as Record<string, unknown>).plan_name ? (
                        <span className="text-sm" style={{ color: "var(--admin-text-primary)" }}>
                          {(p as Record<string, unknown>).plan_name as string}
                        </span>
                      ) : (
                        <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>
                      )}
                    </td>
                    <td>
                      {(p as Record<string, unknown>).representative_name || (p as Record<string, unknown>).representative_phone ? (
                        <span className="text-sm flex items-center gap-1" style={{ color: "var(--admin-text-secondary)" }}>
                          <span className="font-number text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                            #{(p as Record<string, unknown>).representative_user_id as number}
                          </span>
                          {(p as Record<string, unknown>).representative_name ? (
                            <span className="inline-block truncate max-w-[120px] align-middle">{(p as Record<string, unknown>).representative_name as string}</span>
                          ) : (
                            <span dir="ltr" className="font-mono inline-block truncate max-w-[120px] align-middle">{(p as Record<string, unknown>).representative_phone as string}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className="font-number text-sm" style={{ color: "var(--admin-text-primary)" }}>
                        {formatNumber(p.balance as number)}
                      </span>
                    </td>
                    <td>
                      <span className="font-number text-sm" style={{ color: "var(--admin-text-secondary)" }}>
                        {formatNumber(p.api_key_count as number)}
                      </span>
                    </td>
                    <td>
                      <span className="text-sm" style={{ color: "var(--admin-text-secondary)" }}>
                        {p.user_count != null ? formatNumber(p.user_count as number) : "—"}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={p.status as string} />
                    </td>
                    <td className="text-left">
                      <button
                        onClick={() => setSelectedId(p.id as number)}
                        className="p-1.5 rounded-lg transition-colors hover:opacity-70"
                        style={{ color: "var(--admin-primary)" }}
                        title="مشاهده و مدیریت"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t" style={{ borderColor: "var(--admin-border)" }}>
              <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                {formatNumber(total)} شریک
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Create Partner Modal */}
      {showModal && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setShowModal(false); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>شریک جدید</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--admin-text-tertiary)" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نام شریک
                </label>
                <input
                  type="text"
                  value={fields.name}
                  onChange={(e) => setFields({ ...fields, name: e.target.value })}
                  className="admin-input"
                  placeholder="مثلاً شرکت پردازش داده"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  کد یکتا (برای کلید API، لاتین و بدون فاصله)
                </label>
                <input
                  type="text"
                  dir="ltr"
                  value={fields.code}
                  onChange={(e) => setFields({ ...fields, code: e.target.value })}
                  className="admin-input"
                  placeholder="acme-payments"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  موجودی اولیه (ریال)
                </label>
                <input
                  type="text"
                  dir="ltr"
                  value={fields.initial_balance}
                  onChange={(e) => setFields({ ...fields, initial_balance: e.target.value.replace(/[^0-9.]/g, "") })}
                  className="admin-input"
                  placeholder="مثلاً 5000000"
                />
                <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                  موجودی را می‌توانید بعداً از صفحه شریک تغییر دهید.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  پلن اشتراک (اختیاری)
                </label>
                <select
                  value={fields.plan_id}
                  onChange={(e) => setFields({ ...fields, plan_id: e.target.value })}
                  className="admin-input"
                >
                  <option value="">بدون پلن (رایگان)</option>
                  {(plans as Record<string, unknown>[]).map((pl: Record<string, unknown>) => (
                    <option key={pl.plan_id as number} value={String(pl.plan_id as number)}>
                      {pl.name as string}
                    </option>
                  ))}
                </select>
                <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                  کاربران این شریک این پلن را (بدون انقضا / ۱۰ ساله) دریافت می‌کنند.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نماینده (کاربر مسئول شریک، اختیاری)
                </label>
                <RepresentativePicker
                  value={fields.representative_user_id}
                  onChange={(v) => setFields({ ...fields, representative_user_id: v })}
                  placeholder="جستجوی نام، تلفن یا ایمیل کاربر..."
                />
                <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                  یک کاربر موجود را به‌عنوان نمایندهٔ این شریک انتخاب کنید؛ بعداً پنل اختصاصی نماینده برایش فعال می‌شود.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="admin-btn admin-btn-secondary flex-1"
                >
                  انصراف
                </button>
                <button
                  onClick={handleCreate}
                  disabled={createPartner.isPending || !fields.name.trim() || !fields.code.trim()}
                  className="admin-btn admin-btn-primary flex-1"
                >
                  {createPartner.isPending ? "در حال ثبت..." : "ایجاد شریک"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      <PartnerDetailDrawer
        partnerId={selectedId}
        detail={detailQuery.data}
        loading={detailQuery.isLoading}
        canManage={canManage}
        onClose={() => setSelectedId(null)}
        refetchList={refetch}
      />
    </div>
  );
}

interface PartnerDetailDrawerProps {
  partnerId: number | null;
  detail: Record<string, unknown> | undefined;
  loading: boolean;
  canManage: boolean;
  onClose: () => void;
  refetchList: () => void;
}

function PartnerDetailDrawer({ partnerId, detail, loading, canManage, onClose, refetchList }: PartnerDetailDrawerProps) {
  const updatePartner = useAdminUpdatePartner();
  const createKey = useAdminCreatePartnerKey();
  const disableKey = useAdminDisablePartnerKey();
  const deletePartner = useAdminDeletePartner();
  const { data: plans = [] } = useAdminPlans();
  const usersQuery = useAdminPartnerUsers(partnerId);
  const users: Record<string, any>[] = usersQuery.data || [];

  const [balanceField, setBalanceField] = useState("");
  const [shownKey, setShownKey] = useState<string | null>(null);
  const [planChoice, setPlanChoice] = useState<string>("");
  const [repChoice, setRepChoice] = useState<string>("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const partner = partnerId ? ((detail as Record<string, any> | undefined) ?? null) : null;

  useEffect(() => {
    if (partnerId) {
      setShownKey(null);
      setBalanceField("");
    }
  }, [partnerId]);

  // Sync the plan selector whenever the loaded partner detail changes.
  useEffect(() => {
    if (partner) {
      setPlanChoice(partner.plan_id != null ? String(partner.plan_id) : "");
      setRepChoice(partner.representative_user_id != null ? String(partner.representative_user_id) : "");
    }
  }, [partner?.id, partner?.plan_id, partner?.representative_user_id]);

  if (!partnerId) return null;

  const keys: Record<string, any>[] = partner?.api_keys ?? [];
  const userCount = partner?.user_count ?? 0;

  const saveBalance = () => {
    const raw = decimalToPlainString(balanceField);
    const value = Number(raw);
    if (Number.isFinite(value) && value >= 0) {
      updatePartner.mutate(
        { id: partnerId, data: { balance: Math.trunc(value) } },
        { onSuccess: refetchList }
      );
    }
  };

  const savePlan = () => {
    // "" = clear the partner's plan; otherwise assign the chosen plan.
    const plan_id = planChoice === "" ? null : Number(planChoice);
    const body = plan_id === null ? { plan_id: null } : { plan_id };
    updatePartner.mutate({ id: partnerId, data: body });
  };

  const saveRepresentative = () => {
    // "" = clear the representative; otherwise assign the chosen user.
    const representative_user_id = repChoice === "" ? null : Number(repChoice);
    const body = representative_user_id === null
      ? { representative_user_id: null }
      : { representative_user_id };
    updatePartner.mutate({ id: partnerId, data: body });
  };

  const handleDelete = () => {
    deletePartner.mutate(partnerId, {
      onSuccess: onClose,
    });
  };

  const toggleStatus = () => {
    if (!partner) return;
    const next = partner.status === "active" ? "disabled" : "active";
    updatePartner.mutate({ id: partnerId, data: { status: next } });
  };

  const genKey = () => {
    setShownKey(null);
    createKey.mutate(partnerId, {
      onSuccess: (res) => {
        const key = (res as { full_key?: string }).full_key;
        setShownKey(key ?? null);
        refetchList();
      },
    });
  };

  return (
    <div className="admin-overlay">
      <aside className="admin-drawer-panel animate-drawer-left absolute top-0 bottom-0 left-0 w-full md:w-[800px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0" style={{ borderColor: "var(--admin-border)" }}>
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                {partner?.name ?? "..."}
              </h3>
              <code dir="ltr" className="font-mono text-xs" style={{ color: "var(--admin-text-secondary)" }}>
                {partner?.code ?? ""}
              </code>
            </div>
            {partner && <StatusBadge status={partner.status} />}
          </div>
          <button onClick={onClose} className="p-1 rounded-lg transition-colors" style={{ color: "var(--admin-text-tertiary)" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading || !partner ? (
            <div className="flex items-center justify-center py-16">
              <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : (
            <>
              {/* Assigned Plan */}
              <div className="admin-card">
                <div className="admin-card-header">
                  <h4 className="admin-card-title">پلن اشتراک</h4>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: "var(--admin-text-secondary)" }}>پلن فعلی</span>
                    <span className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>
                      {partner.plan_name ? (partner.plan_name as string) : "بدون پلن (رایگان)"}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                    با انتخاب پلن، همه کاربران مرتبط با این شریک این پلن را دریافت می‌کنند و پلن قبلی آن‌ها (بدون انقضا / ۱۰ ساله) جایگزین می‌شود.
                  </p>
                  {canManage && (
                    <div className="flex items-center gap-2 pt-1">
                      <select
                        value={planChoice}
                        onChange={(e) => setPlanChoice(e.target.value)}
                        className="admin-input"
                      >
                        <option value="">بدون پلن (رایگان)</option>
                        {(plans as Record<string, unknown>[]).map((pl: Record<string, unknown>) => (
                          <option key={pl.plan_id as number} value={String(pl.plan_id as number)}>
                            {pl.name as string}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={savePlan}
                        disabled={updatePartner.isPending}
                        className="admin-btn admin-btn-primary shrink-0"
                      >
                        {updatePartner.isPending ? "..." : "اعمال پلن"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Representative */}
              <div className="admin-card">
                <div className="admin-card-header">
                  <h4 className="admin-card-title">نماینده</h4>
                </div>
                <div className="p-4 space-y-3">
                  {partner.representative_user_id ? (
                    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                      <div className="min-w-0 text-right">
                        <span className="block text-sm font-medium truncate" style={{ color: "var(--admin-text-primary)" }}>
                          <span className="font-number">#{partner.representative_user_id}</span>{" "}
                          {partner.representative_name || "—"}
                        </span>
                        {partner.representative_phone && (
                          <span dir="ltr" className="block font-mono text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                            {partner.representative_phone}
                          </span>
                        )}
                      </div>
                      <span className="admin-badge admin-badge-success shrink-0" style={{ fontSize: 10 }}>نماینده</span>
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>
                      هنوز نماینده‌ای انتخاب نشده است.
                    </p>
                  )}
                  <p className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                    نماینده یک کاربر موجود از پلتفرم است و به پنل اختصاصی «داشبورد همکاران» (مسیر /partners/dashboard) دسترسی دارد.
                  </p>
                  {canManage && (
                    <div className="space-y-2 pt-1">
                      <RepresentativePicker
                        value={repChoice}
                        onChange={setRepChoice}
                        placeholder="تغییر نماینده — جستجوی کاربر..."
                      />
                      <button
                        onClick={saveRepresentative}
                        disabled={updatePartner.isPending || String(partner.representative_user_id ?? "") === repChoice}
                        className="admin-btn admin-btn-primary"
                      >
                        {updatePartner.isPending ? "..." : repChoice === "" ? "حذف نماینده" : "اعمال نماینده"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Balance */}
              <div className="admin-card">
                <div className="admin-card-header">
                  <h4 className="admin-card-title">موجودی</h4>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: "var(--admin-text-secondary)" }}>موجودی فعلی</span>
                    <span className="font-number text-lg font-bold" style={{ color: "var(--admin-text-primary)" }}>
                      {formatNumber(partner.balance)} <span className="text-xs font-normal" style={{ color: "var(--admin-text-tertiary)" }}>ریال</span>
                    </span>
                  </div>
                  <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>کاربران: {formatNumber(userCount)}</span>
                  {canManage && (
                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="text"
                        dir="ltr"
                        value={balanceField}
                        onChange={(e) => setBalanceField(e.target.value.replace(/[^0-9.]/g, ""))}
                        className="admin-input"
                        placeholder="موجودی جدید (ریال)"
                      />
                      <button
                        onClick={saveBalance}
                        disabled={updatePartner.isPending || !balanceField}
                        className="admin-btn admin-btn-primary shrink-0"
                      >
                        {updatePartner.isPending ? "..." : "ثبت موجودی"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Users */}
              <div className="admin-card">
                <div className="admin-card-header">
                  <h4 className="admin-card-title">کاربران ({formatNumber(userCount)})</h4>
                </div>
                {usersQuery.isLoading ? (
                  <div className="p-4 text-sm" style={{ color: "var(--admin-text-tertiary)" }}>
                    در حال خواندن کاربران...
                  </div>
                ) : !users.length ? (
                  <div className="p-4 text-sm" style={{ color: "var(--admin-text-tertiary)" }}>
                    هنوز کاربری وصل نشده است.
                  </div>
                ) : (
                  <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                    {users.map((u) => (
                      <div key={u.user_id as number} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-number text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>
                            #{u.user_id as number}
                          </span>
                          <code dir="ltr" className="font-mono text-xs truncate" style={{ color: "var(--admin-text-secondary)" }}>
                            {u.external_user_identifier ?? "—"}
                          </code>
                          {u.phone && (
                            <code dir="ltr" className="font-mono text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                              ({u.phone})
                            </code>
                          )}
                        </div>
                        <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                          {u.created_at ? new Date(u.created_at as string).toLocaleDateString("fa-IR") : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Status / Danger zone */}
              {canManage && (
                <div className="admin-card">
                  <div className="admin-card-header">
                    <h4 className="admin-card-title">وضعیت</h4>
                  </div>
                  <div className="p-4">
                    <button onClick={toggleStatus} className="admin-btn admin-btn-secondary">
                      {partner.status === "active" ? "غیرفعال کردن شریک" : "فعال کردن شریک"}
                    </button>
                  </div>
                  <div className="p-4" style={{ borderTop: "1px solid var(--admin-border)" }}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>
                          حذف شریک
                        </p>
                        <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                          کلیدها و دسترسی کاربران این شریک حذف می‌شود؛ کاربران و اشتراک‌های قبلی آن‌ها باقی می‌ماند.
                        </p>
                      </div>
                      {!confirmDelete ? (
                        <button
                          onClick={() => setConfirmDelete(true)}
                          className="admin-btn admin-btn-danger shrink-0"
                        >
                          حذف
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={handleDelete}
                            disabled={deletePartner.isPending}
                            className="admin-btn admin-btn-danger"
                          >
                            {deletePartner.isPending ? "در حال حذف..." : "تایید حذف"}
                          </button>
                          <button
                            onClick={() => setConfirmDelete(false)}
                            className="admin-btn admin-btn-secondary"
                          >
                            انصراف
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* API Keys */}
              <div className="admin-card">
                <div className="admin-card-header flex items-center justify-between">
                  <h4 className="admin-card-title">کلیدهای API</h4>
                  {canManage && (
                    <button onClick={genKey} className="admin-btn admin-btn-primary text-xs" style={{ padding: "6px 12px" }}>
                      {createKey.isPending ? "در حال ساخت..." : "+ کلید جدید"}
                    </button>
                  )}
                </div>

                {shownKey && (
                  <div className="p-4 space-y-2" style={{ background: "rgba(250, 204, 21, 0.07)", borderBottom: "1px solid rgba(250, 204, 21, 0.2)" }}>
                    <p className="text-xs font-medium" style={{ color: "var(--admin-warning)" }}>
                      کلید فقط یک‌بار نمایش داده می‌شود — آن را کپی کنید.
                    </p>
                    <div className="flex items-center gap-2">
                      <code dir="ltr" className="font-mono text-xs break-all flex-1" style={{ background: "var(--admin-bg-hover)", padding: "8px 10px", borderRadius: 8 }}>
                        {shownKey}
                      </code>
                      <SimpleCopyButton text={shownKey} />
                    </div>
                  </div>
                )}

                {!keys.length ? (
                  <div className="p-4 text-sm" style={{ color: "var(--admin-text-tertiary)" }}>
                    هنوز کلیدی ساخته نشده است.
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    {keys.map((k) => (
                      <div key={k.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                        <code dir="ltr" className="font-mono text-xs" style={{ color: "var(--admin-text-primary)" }}>
                          {k.key_prefix}
                        </code>
                        <div className="flex items-center gap-2">
                          <KeyStatusBadge status={k.status} />
                          {canManage && k.status === "active" && (
                            <button
                              onClick={() => disableKey.mutate({ partnerId, keyId: k.id })}
                              className="text-xs"
                              style={{ color: "var(--admin-error)" }}
                            >
                              غیرفعال
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}